import { BACKEND_BASE_URL } from '@/app/config.js';
import {
  emitMembershipNotAccepted,
  emitTermsRequired,
  emitUnauthorized,
} from './authEvents.js';
import { apiErrorName } from './apiErrorName.js';
import { getOrganizationId } from './orgContext.js';

// The single client for the recepthor-api backend. Everything the web app needs
// to talk to the backend lives here: the fetch transport (apiClient), the
// authenticated endpoint wrappers, and the public pre-session auth calls.
// There is one recepthor-api; "receptor" and "solicitante" are actors of the
// same app, not separate clients (the former per-actor service modules —
// lawyer/receptor/auth/feedback — were merged here).

// Authenticated transport. Cookies carry the session; the active org rides the
// X-Organization-Id header. A 401 means the session died — signal it centrally
// so AuthContext can decide whether to act (it ignores the signal when there is
// no active session, e.g. public endpoints).
export async function apiClient(endpoint, options = {}) {
  const { headers, ...rest } = options;
  const orgId = getOrganizationId();

  const response = await fetch(`${BACKEND_BASE_URL}${endpoint}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(orgId && { 'X-Organization-Id': orgId }),
      ...headers,
    },
    ...rest,
  });

  if (!response.ok) {
    await throwApiError(response);
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

// Shared non-ok handling for authenticated fetches: signal a dead session
// (401) or a session the terms gate refuses (403 TermsNotAccepted), surface
// the API's error message, throw a status-tagged Error.
async function throwApiError(response) {
  if (response.status === 401) {
    emitUnauthorized();
  }

  let message = `Request failed: ${response.status}`;
  let body = null;
  try {
    body = await response.json();
    // AppError on the API serializes as { error: { message, name, statusCode }, success: false }.
    // A few legacy endpoints still return { message } at the top level, so keep that fallback.
    if (body?.error?.message) message = body.error.message;
    else if (body?.message) message = body.message;
  } catch {
    // no parseable body — keep default message
  }
  if (response.status === 403 && apiErrorName(body) === 'TermsNotAccepted') {
    emitTermsRequired();
  }
  if (
    response.status === 403 &&
    apiErrorName(body) === 'MembershipNotAccepted'
  ) {
    emitMembershipNotAccepted();
  }
  const error = new Error(message);
  error.status = response.status;
  error.body = body;
  throw error;
}

export { apiErrorName };

// User-facing message for an apiClient error: 4xx responses carry an
// actionable message from the API (surface it); anything else falls back to
// the caller's generic copy.
export function errorMessage(err, fallback) {
  const status = err?.status;
  return status >= 400 && status < 500 && err?.message ? err.message : fallback;
}

// All recepthor-api routes are mounted under /api/* on a flat namespace,
// with no version prefix.
const BASE_PATH = '/api';

// Public email credential: never attach session cookies, an organization or
// emitUnauthorized. Rate limiting returns plain text, not the JSON envelope.
export async function getQuoteByToken(token) {
  const response = await fetch(
    `${BACKEND_BASE_URL}${BASE_PATH}/quotes/by-token`,
    {
      credentials: 'omit',
      referrerPolicy: 'strict-origin',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (response.ok) return response.json();
  const error = new Error('No pudimos cargar la solicitud.');
  error.status = response.status;
  if (response.status !== 429) {
    const body = await response.json().catch(() => null);
    error.name = apiErrorName(body) || 'Error';
  }
  throw error;
}

export async function acceptQuoteByToken(token, payload) {
  const response = await fetch(
    `${BACKEND_BASE_URL}${BASE_PATH}/quotes/accept`,
    {
      method: 'POST',
      credentials: 'omit',
      referrerPolicy: 'strict-origin',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  // La oferta ya quedó registrada: un cuerpo ilegible no la vuelve un error.
  if (response.ok) return response.json().catch(() => ({}));
  const error = new Error('No pudimos enviar la oferta. Inténtalo de nuevo.');
  error.status = response.status;
  if (response.status !== 429) {
    const body = await response.json().catch(() => null);
    error.name = apiErrorName(body) || 'Error';
  }
  throw error;
}

function request(endpoint, options = {}) {
  return apiClient(`${BASE_PATH}${endpoint}`, options);
}

function buildSearch(params) {
  const filtered = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null && v !== 'undefined'),
  );
  return new URLSearchParams(filtered).toString();
}

// Explicit-org options fragment. The org header
// normally rides the orgContext module singleton (apiClient) — a side-channel
// that can be unset while the caller, gated on useAuth().organizationId,
// believes an org is active (module re-execution under HMR, mount-order
// races), and the API then 400s with OrganizationHeaderMissing. Spreading
// this into a wrapper's request options pins the request to the org the
// caller resolved; explicit headers win the apiClient merge, and without
// organizationId the singleton fallback applies unchanged.
function orgScope(organizationId) {
  return organizationId
    ? { headers: { 'X-Organization-Id': organizationId } }
    : {};
}

// -- Cases --

export function getCases({
  page,
  pageSize,
  search,
  sortBy,
  sortKey,
  tribunalId,
  courtId,
  competence,
  // Estado de la diligencia activa de la causa: un TaskStatus, o el centinela
  // 'NONE' (causas sin diligencia activa).
  diligenciaStatus,
  legalStatus, // 'Tramitación' | 'Terminada' etc.
  assignee, // 'me' para asignadas al usuario actual
  // 'true' | 'false'. The server's one active-case criterion: not deleted,
  // legalStatus neither 'Terminada' nor null, verification outside NOT_EXIST
  // and VERIFIED_FAIL.
  active,
  organizationId,
}) {
  const qs = buildSearch({
    currentPage: String(page),
    pageSize: String(pageSize),
    search,
    sortBy,
    sortKey,
    tribunalId,
    courtId,
    competence,
    diligenciaStatus,
    legalStatus,
    assignee,
    active,
  });
  return request(`/cases?${qs}`, orgScope(organizationId));
}

export function getCase(caseId, { organizationId } = {}) {
  return request(
    `/cases/${encodeURIComponent(caseId)}`,
    orgScope(organizationId),
  );
}

export function createCase(data, { organizationId } = {}) {
  return request('/cases', {
    method: 'POST',
    body: JSON.stringify(data),
    ...orgScope(organizationId),
  });
}

// Re-scrape a causa from PJUD. `force` (manual "sincronizar ahora") relaunches
// regardless of state; without it the backend only relaunches never-scraped or
// stuck causas. Returns the last-scrape state ({ relaunched, verificationStatus,
// syncedAt, scrapeRequestedAt, scrapeDetail }).
export function ensureCaseScrape(
  caseId,
  { force = false, organizationId } = {},
) {
  return request(`/cases/${encodeURIComponent(caseId)}/scrape`, {
    method: 'POST',
    body: JSON.stringify({ force }),
    ...orgScope(organizationId),
  });
}

export function deleteCase(caseId, { organizationId } = {}) {
  return request(`/cases/${encodeURIComponent(caseId)}`, {
    method: 'DELETE',
    ...orgScope(organizationId),
  });
}

// PATCH /cases/:caseId/assignee — asignar (o desasignar con null) el encargado.
// Acepta { assigneeId } (membership id string, o null para desasignar).
export function assignCaseAssignee(
  caseId,
  assigneeId,
  { organizationId } = {},
) {
  return request(`/cases/${encodeURIComponent(caseId)}/assignee`, {
    method: 'PATCH',
    body: JSON.stringify({ assigneeId }),
    ...orgScope(organizationId),
  });
}

// -- Tasks --

export function getActiveTask({
  caseId,
  page = 1,
  pageSize = 10,
  organizationId,
}) {
  const search = buildSearch({
    caseId,
    currentPage: String(page),
    pageSize: String(pageSize),
  });
  return request(`/tasks/active?${search}`, orgScope(organizationId));
}

export function getTasks({
  page = 1,
  // Bounded default: every list view pages or caps its own fetch, and the
  // fetch-everything 999 this replaced is what pinned the server's pageSize
  // cap. Callers that need more rows pass their own (bounded) pageSize.
  pageSize = 50,
  search,
  sortBy,
  sortKey,
  // Multi-key sort (Diligencias v2): CSV of `key:dir` in priority order, e.g.
  // 'deadline:asc,updatedAt:desc'. When present the server uses it over
  // sortKey/sortBy. Keys are whitelisted server-side (unknown ones ignored).
  sort,
  status,
  // Lane filter for the Diligencias v2 work surface: 'bandeja' | 'borradores' |
  // 'enCurso' | 'historial'. The server decides lane membership (incl. the
  // derived "Expirada"); the frontend never classifies. Omitted ⇒ unfiltered (v1).
  lane,
  // Scope the list to one case server-side, instead of over-fetching the org's
  // tasks and filtering client-side.
  caseId,
  organizationId,
} = {}) {
  const qs = buildSearch({
    currentPage: String(page),
    pageSize: String(pageSize),
    search,
    sort,
    sortBy,
    sortKey,
    status,
    lane,
    caseId,
  });
  return request(`/tasks?${qs}`, orgScope(organizationId));
}

// GET /tasks/lane-counts — the per-lane totals for the Diligencias v2 tab
// badges: { bandeja, enProceso, historial }. Mirrors getTasks: the active org
// rides the X-Organization-Id header (injected by apiClient), and `search`
// is optional (Historial badge can reflect the active search). Not paginated.
export function getLaneCounts({ search, organizationId } = {}) {
  const qs = buildSearch({ search });
  return request(
    `/tasks/lane-counts${qs ? `?${qs}` : ''}`,
    orgScope(organizationId),
  );
}

export function createTask(data, { organizationId } = {}) {
  return request('/tasks', {
    method: 'POST',
    body: JSON.stringify(data),
    ...orgScope(organizationId),
  });
}

// Diligencia by id — hidrata la vista by-id (/diligencias/:id). Endpoint real.
export function getTask(taskId, { organizationId } = {}) {
  return request(
    `/tasks/${encodeURIComponent(taskId)}`,
    orgScope(organizationId),
  );
}

// ── Diligencia "por corte" + borrador persistido ──────────────────────────
// createDiligenciaByCorte: POST /tasks/by-court (los receptores se derivan del
// corte; NO se mandan receptorIds). Campos: caseId, commune (NO comuna), region,
// address (no vacíos), addressDetails (nullable), deadline (ISO 8601), notes.
// El endpoint envía a cotizar siempre al crear: no existe un flag para dejar
// la diligencia en DRAFT, y por eso el flujo de creación no lo llama todavía.
export function createDiligenciaByCorte(data, { organizationId } = {}) {
  return request('/tasks/by-court', {
    method: 'POST',
    body: JSON.stringify(data),
    ...orgScope(organizationId),
  });
}

// PENDING backend (§2 del spec): PATCH /tasks/:taskId — editar la notificación
// mientras la diligencia está DRAFT. NO existe aún; no llamar hasta que aterrice.
export function updateTask(taskId, data, { organizationId } = {}) {
  return request(`/tasks/${encodeURIComponent(taskId)}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
    ...orgScope(organizationId),
  });
}

// PENDING backend (§3 del spec): POST /tasks/:taskId/send — enviar un DRAFT a
// cotizar (DRAFT → emails → QUOTING). NO existe aún.
export function sendDiligencia(taskId, { organizationId } = {}) {
  return request(`/tasks/${encodeURIComponent(taskId)}/send`, {
    method: 'POST',
    ...orgScope(organizationId),
  });
}

// POST /tasks/:taskId/cancel — cancelación por el estudio de una diligencia
// previa al pago (DRAFT/QUOTING/PROCESSING_PAYMENT sin payin confirmado →
// CANCELLED). Libera la causa. `reason` es opcional (auditoría).
export function cancelTask(taskId, { reason, organizationId } = {}) {
  return request(`/tasks/${encodeURIComponent(taskId)}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    ...orgScope(organizationId),
  });
}

// -- Quotes --

export function getQuotes({
  taskId,
  status,
  scope,
  page,
  pageSize,
  sortBy,
  sortKey,
  organizationId,
}) {
  const search = buildSearch({
    taskId,
    // Org-wide filter (e.g. status=ACCEPTED feeds the Pagos "Por pagar" queue).
    status,
    // Owning task's stage, orthogonal to the quote's own status. Absent, no
    // stage narrows the listing.
    scope,
    currentPage: String(page),
    pageSize: String(pageSize),
    sortBy,
    sortKey,
  });
  return request(`/quotes?${search}`, orgScope(organizationId));
}

export function getQuote(quoteId, { organizationId } = {}) {
  return request(
    `/quotes/${encodeURIComponent(quoteId)}`,
    orgScope(organizationId),
  );
}

// `items` prices the offer per recipient (REC-1983); `price` is then their
// sum, which the API checks.
export function acceptQuote(
  { quoteId, price, dateOffered, items },
  { organizationId } = {},
) {
  return request('/quotes/accept', {
    method: 'POST',
    body: JSON.stringify({
      quoteId,
      price,
      dateOffered,
      ...(items ? { items } : {}),
    }),
    ...orgScope(organizationId),
  });
}

export function rejectQuote({ quoteId }, { organizationId } = {}) {
  return request('/quotes/reject', {
    method: 'POST',
    body: JSON.stringify({ quoteId }),
    ...orgScope(organizationId),
  });
}

// -- Payments --

// GET /payments — the org's money movements, one row per movement: a task's
// payin and payout are separate rows. The row carries no Case/Tribunal fields
// by design; callers correlate task-side data through GET /tasks by taskId.
export function getPayments({
  taskId,
  // PaymentType filter: PAYIN | PAYOUT | REFUND_PAYIN | REFUND_PAYOUT.
  type,
  // PaymentStatus filter: PENDING | PROCESSING | FAILED | SUCCESS. SUCCESS is
  // provider-confirmed money — the source of the Comprobantes de pago tab.
  status,
  page,
  pageSize,
  sortBy,
  sortKey,
  organizationId,
} = {}) {
  const search = buildSearch({
    taskId,
    type,
    status,
    currentPage: String(page),
    pageSize: String(pageSize),
    sortBy,
    sortKey,
  });
  return request(`/payments?${search}`, orgScope(organizationId));
}

export function getPaymentMethod({ organizationId } = {}) {
  return request('/payments/payin/payment-method', orgScope(organizationId));
}

// Expects: { billingProfileId, quoteId }
// Backend reads billing data from the organization's BillingProfile.
export function createPayment(data, { organizationId } = {}) {
  return request('/payments/payin', {
    method: 'POST',
    body: JSON.stringify(data),
    ...orgScope(organizationId),
  });
}

export function deletePaymentMethod(id, { organizationId } = {}) {
  return request(`/payments/payin/payment-method/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    ...orgScope(organizationId),
  });
}

export function getInvoices({ organizationId } = {}) {
  return request('/payments/payin/invoices', orgScope(organizationId));
}

// Credentialed fetch of a document the org middleware gates. A plain
// href/window.open can't carry the X-Organization-Id header that middleware
// requires, so every document read goes through here.
//
// The endpoint MUST return the bytes directly (200), not a 302 to a presigned
// S3 URL: this fetch is credentialed (`credentials: 'include'`) for the org
// middleware, and a credentialed fetch cannot follow a cross-origin redirect
// into S3 — the browser's CORS check requires `Access-Control-Allow-Credentials`
// on the redirected response, which S3 bucket CORS cannot emit, so the read
// fails regardless of AllowedOrigins.
async function fetchOrgBlob(path, { organizationId } = {}) {
  const orgId = organizationId ?? getOrganizationId();
  const response = await fetch(`${BACKEND_BASE_URL}${BASE_PATH}${path}`, {
    credentials: 'include',
    ...orgScope(orgId),
  });
  if (!response.ok) {
    await throwApiError(response);
  }
  return response.blob();
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// GET /payments/payin/:paymentId/receipt — the platform's own comprobante PDF
// for a settled PAYIN movement. Callers gate the action on the movement's
// `hasReceipt`, which says the comprobante is archived.
export async function downloadPayinReceipt(
  paymentId,
  { organizationId, filename } = {},
) {
  const blob = await fetchOrgBlob(
    `/payments/payin/${encodeURIComponent(paymentId)}/receipt`,
    { organizationId },
  );
  saveBlob(blob, filename ?? `comprobante-${paymentId}.pdf`);
}

// GET /tax-documents/:id/pdf — bytes of the SII invoice. Same credentialed
// fetch + blob save as the platform comprobante: a plain href cannot carry
// X-Organization-Id.
export async function downloadTaxDocumentPdf(
  id,
  { organizationId, filename } = {},
) {
  const blob = await fetchOrgBlob(
    `/tax-documents/${encodeURIComponent(id)}/pdf`,
    { organizationId },
  );
  saveBlob(blob, filename ?? `factura-${id}.pdf`);
}

// Opens the SII invoice in a new tab. The tab is opened BEFORE the fetch, and
// this order is the whole point: a browser grants the popup on the click's
// transient user activation, which expires while the request is in flight, so
// opening afterwards gets blocked. Opening first also makes the block
// observable: `window.open` returns null only when it was refused. Opening
// it with `noopener` would return null on success too, and no caller could
// tell the two apart.
//
// Chromium will not navigate a window to a blob: URL after `opener` is
// nulled (the blob is bound to this document). Assign the blob first, then
// clear opener so the tab cannot reach back. Clearing it first leaves
// about:blank and throws.
//
// A blocked popup and a blob the tab cannot navigate both fall through to
// the same download: the click asked for the PDF, and the file is what we
// can still give.
export async function viewTaxDocumentPdf(
  id,
  { organizationId, filename } = {},
) {
  const downloadName = filename ?? `factura-${id}.pdf`;
  const tab = window.open('', '_blank');
  let blob;
  try {
    blob = await fetchOrgBlob(`/tax-documents/${encodeURIComponent(id)}/pdf`, {
      organizationId,
    });
  } catch (err) {
    // A tab left open on about:blank reads as a broken action; the error
    // belongs in the page the user is looking at.
    tab?.close();
    throw err;
  }
  if (!tab) {
    saveBlob(blob, downloadName);
    return;
  }
  const url = URL.createObjectURL(blob);
  try {
    tab.location = url;
    tab.opener = null;
  } catch {
    tab.close();
    saveBlob(blob, downloadName);
    URL.revokeObjectURL(url);
    return;
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function downloadTaxDocumentXml(
  id,
  { organizationId, filename } = {},
) {
  const blob = await fetchOrgBlob(
    `/tax-documents/${encodeURIComponent(id)}/xml`,
    { organizationId },
  );
  saveBlob(blob, filename ?? `factura-${id}.xml`);
}

// -- Payouts (plataforma / backoffice) --

// GET /payments/payout/queue — cola CROSS-ORG de payouts (solo backoffice).
// `filter`, medido contra el where del servidor y no contra su docblock, que
// dice otra cosa: 'actionable' es payin SUCCESS + payout PENDING, o sea la
// cola limpia; 'history' es payout PROCESSING o SUCCESS; 'incomplete' junta lo
// que falló (payin o payout en FAILED) con lo atascado por antigüedad. Un
// payout fallido cae en 'incomplete', nunca en 'actionable'. Forma
// { pagination, results } — consumible por useLedgerQuery, con total honesto
// por filtro.
export function getPayoutQueue({
  page = 1,
  pageSize = 50,
  filter = 'actionable',
  organizationId,
} = {}) {
  const qs = buildSearch({
    currentPage: String(page),
    pageSize: String(pageSize),
    filter,
  });
  return request(`/payments/payout/queue?${qs}`, orgScope(organizationId));
}

// GET /channels/intakes — pedidos WhatsApp CROSS-ORG (solo backoffice).
// `filter`: pending | accepted | rejected. Shape { pagination, results }.
export function getChannelIntakes({
  page = 1,
  pageSize = 50,
  filter = 'pending',
  organizationId,
} = {}) {
  const qs = buildSearch({
    currentPage: String(page),
    pageSize: String(pageSize),
    filter,
  });
  return request(`/channels/intakes?${qs}`, orgScope(organizationId));
}

// Catalog: GET /channels/terms/current. GATE lookup (alreadyAccepted for
// this E.164 + current version): GET /channels/terms/lookup?identifier=
export function getCurrentChannelTerms({ organizationId, identifier } = {}) {
  if (identifier) {
    const qs = buildSearch({ identifier });
    return request(`/channels/terms/lookup?${qs}`, orgScope(organizationId));
  }
  return request('/channels/terms/current', orgScope(organizationId));
}

export function acceptChannelIntake(id, { organizationId, body } = {}) {
  const endpoint = `/channels/intakes/${encodeURIComponent(id)}/accept`;
  return request(endpoint, {
    method: 'POST',
    ...orgScope(organizationId),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
}

export function rejectChannelIntake(id, { organizationId } = {}) {
  return request(`/channels/intakes/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    ...orgScope(organizationId),
  });
}

// GET /channels/intakes/:id — one intake of the cola with its linked case, live
// task and charge (REC-2041). Read-only: no Toku call, nothing enqueued.
export function getChannelIntakeDetail(id, { organizationId } = {}) {
  return request(
    `/channels/intakes/${encodeURIComponent(id)}`,
    orgScope(organizationId),
  );
}

// POST /payments/payin/channel — starts (or resumes) the charge of a channel
// diligence over the chosen quote and answers the Checkout Session. Idempotent
// on the live PAYIN, but a lapsed session is replaced by a new one, so it is
// not a refresh.
export function initiateChannelPayin(quoteId, { organizationId } = {}) {
  return request('/payments/payin/channel', {
    method: 'POST',
    body: JSON.stringify({ quoteId }),
    ...orgScope(organizationId),
  });
}

// POST /tasks/by-channel-intake — creates a diligence per address on the case
// an accepted channel intake linked, and sends it to quote (REC-1400). `body`
// is { intakeId, addresses[1..10] } with { address, commune, region, deadline,
// addressDetails?, notes? } each, and goes as is: the API is strict about
// unknown keys. Answers { caseId, message, diligences[] }, one per address.
export function createChannelDiligence(body, { organizationId } = {}) {
  return request('/tasks/by-channel-intake', {
    method: 'POST',
    body: JSON.stringify(body),
    ...orgScope(organizationId),
  });
}

// GET /platform/quotes?taskId= — the quotes of a diligence, any firm
// (backoffice, `quote:platform-read`). Shape { pagination, results }.
export function getPlatformQuotes({
  taskId,
  page = 1,
  pageSize = 50,
  organizationId,
} = {}) {
  const qs = buildSearch({
    taskId,
    currentPage: String(page),
    pageSize: String(pageSize),
  });
  return request(`/platform/quotes?${qs}`, orgScope(organizationId));
}

// GET /payments/webhook-events — what the provider sent, with the processing
// outcome of each delivery. Platform (backoffice) view, cross-org, gated on
// payout:manual. The column's five statuses: RECEIVED on insert, DUPLICATE when
// a re-delivery carries an effect already applied, PROCESSED as terminal, and
// FAILED with IGNORED, which are the two a reprocess attacks. Shape
// { pagination, results } — consumable by useLedgerQuery.
export function getWebhookEvents({
  page = 1,
  pageSize = 50,
  status,
  organizationId,
} = {}) {
  const qs = buildSearch({
    currentPage: String(page),
    pageSize: String(pageSize),
    ...(status ? { status } : {}),
  });
  return request(`/payments/webhook-events?${qs}`, orgScope(organizationId));
}

// POST /payments/webhook-events/:eventId/reprocess — replays the event's stored
// body through the same router that received it the first time. There is no
// second processing path, so a reprocess cannot apply anything other than what
// the event said. Idempotency comes from the gates of the resources the event
// touches, not from a status check: reprocessing one already applied moves zero
// rows.
export function reprocessWebhookEvent(eventId, { organizationId } = {}) {
  return request(
    `/payments/webhook-events/${encodeURIComponent(eventId)}/reprocess`,
    { method: 'POST', ...orgScope(organizationId) },
  );
}

// Global platform settings; organization context authenticates the member.
// Both return `{ slackUserIds }`, unwrapped from the `{ data }` envelope; an
// empty (204) response stays null.
export async function getPlatformAttentionSettings({ organizationId } = {}) {
  const res = await request(
    '/platform/settings/attention',
    orgScope(organizationId),
  );
  return res?.data ?? res;
}

export async function updatePlatformAttentionSettings(
  slackUserIds,
  { organizationId } = {},
) {
  const res = await request('/platform/settings/attention', {
    method: 'PUT',
    body: JSON.stringify({ slackUserIds }),
    ...orgScope(organizationId),
  });
  return res?.data ?? res;
}

// GET /platform/settings/offer-link-target — where the quote request email
// links to: 'fillout' (the form) or 'web' (/oferta). `source` says whether a
// saved setting decides or the API falls back to its environment. The API
// reads the setting on every send, with a cache of about 30 s per instance.
export async function getOfferLinkTarget({ organizationId } = {}) {
  const res = await request(
    '/platform/settings/offer-link-target',
    orgScope(organizationId),
  );
  return res?.data ?? res;
}

// PUT /platform/settings/offer-link-target — saves the target. Links already
// sent keep working either way: both carry the same quote token.
export async function updateOfferLinkTarget(target, { organizationId } = {}) {
  const res = await request('/platform/settings/offer-link-target', {
    method: 'PUT',
    body: JSON.stringify({ target }),
    ...orgScope(organizationId),
  });
  return res?.data ?? res;
}

// GET /payments/payin/unmatched — the payment intents the provider charged whose
// invoice matched no deal. The payin webhook writes them when an intent resolves
// NO_DEAL, so every row is money that came in and that nothing applied. Platform
// (backoffice) view, cross-org, gated on payout:manual. Answers a bare array
// newest first: it neither paginates nor filters, because the reconciliation
// queue is short by definition.
export function getUnmatchedPayments({ organizationId } = {}) {
  return request('/payments/payin/unmatched', orgScope(organizationId));
}

// GET /tax-documents?folio=N — the emitted facturas that carry one folio,
// cross-org, newest first, with their organization (id and name), a masked
// receiver and the credit notes that correct each one (REC-1957).
// Platform (backoffice), gated on payout:manual. A folio is unique per issuer
// and DTE type, not a key, so the answer is a list and the annulment below
// takes the id of the one the operator picks.
export function findTaxDocumentsByFolio(folio, { organizationId } = {}) {
  return request(
    `/tax-documents?${buildSearch({ folio })}`,
    orgScope(organizationId),
  );
}

// POST /tax-documents/:id/annul — annuls one emitted factura with a credit
// note (SII CodRef 1) for what remains of it. A dry run unless the body says
// `dryRun: false`, and then it needs a `reason`. Idempotent: an annulment that
// already exists is answered (`already_annulled`, `annulment_in_flight`), not
// duplicated. 201 only when this call created the note.
export function annulTaxDocument(
  id,
  { dryRun = true, motivo, reason } = {},
  { organizationId } = {},
) {
  return request(`/tax-documents/${encodeURIComponent(id)}/annul`, {
    method: 'POST',
    body: JSON.stringify({ dryRun, motivo, reason }),
    ...orgScope(organizationId),
  });
}

// POST /payments/payout. Fires the Shinkansen payout for ONE quote. The transfer
// leaves and the lane waits for the confirmation webhook. The quote holds at most
// one live payout attempt, so it names the attempt without a second id.
export function initiatePayout(quoteId, { organizationId } = {}) {
  return request('/payments/payout', {
    method: 'POST',
    body: JSON.stringify({ quoteId }),
    ...orgScope(organizationId),
  });
}

// POST /payments/payout/:paymentId/retry — abre el intento siguiente de un
// payout fallido, copiando monto y cuenta de la fila que falló. No reinstruye
// la fallida: la máquina no declara salida desde FAILED, a propósito, para que
// ese intento conserve su motivo y su historia. Devuelve el id del movimiento
// nuevo, que nace PENDING y recién ahí es instruible.
export function retryPayout(paymentId, { organizationId } = {}) {
  return request(`/payments/payout/${encodeURIComponent(paymentId)}/retry`, {
    method: 'POST',
    ...orgScope(organizationId),
  });
}

// POST /payments/payout/:paymentId/close — gives an exit to a payout left in
// flight that the provider never answered. It is the only exit from in-flight
// that is not a webhook: the machine declares no other, on purpose, so without
// this the money sits in a state nobody can move it out of.
//
// The body says whether the transfer happened, not which status to write:
//   · OCCURRED     → the movement settles and the lane closes.
//   · NOT_OCCURRED → the movement fails, and a failed attempt is what the retry
//                    needs, so the row starts offering one.
//
// Admitted only past the staleness threshold. Inside it the answer is 409
// PayoutNotStuck, because the provider can still report.
export function closeStuckPayout(paymentId, outcome, { organizationId } = {}) {
  return request(`/payments/payout/${encodeURIComponent(paymentId)}/close`, {
    method: 'POST',
    body: JSON.stringify({ outcome }),
    ...orgScope(organizationId),
  });
}

// POST /payments/payout/:paymentId/settle-manual — registra que un operario de
// Recepthor transfirió a mano un payout que ya fue instruido al proveedor. Es
// la vía que el registro por lote no alcanza: aquel entra por INSTRUCT, que
// solo sale de un movimiento pendiente, y este parte de uno en vuelo.
//
// Nombra el movimiento y no la diligencia. Una diligencia con reintento carga
// un intento fallido y uno vivo a la vez, así que su id no dice cuál de los dos
// se está liquidando y el servidor tendría que adivinarlo.
//
// Sin umbral de vejez, a diferencia del cierre: aquel decide sobre lo que hizo
// el proveedor y espera la ventana en que todavía puede responder, este
// registra lo que un operario ya hizo. Liquida el movimiento existente, así que
// la fila sale de incompletos y entra en el historial.
// `paidAt` es cuándo transfirió el operario, ISO 8601 **con offset**, y es
// opcional: ausente cae al instante del registro. Se manda porque esa fecha se
// muestra en el panel, así que escribir el instante del registro mostraría una
// fecha que sabemos equivocada. En el cierre no aplica: ahí nadie sabe cuándo
// movió la plata el proveedor.
export function settleManualPayout(paymentId, { organizationId, paidAt } = {}) {
  return request(
    `/payments/payout/${encodeURIComponent(paymentId)}/settle-manual`,
    {
      method: 'POST',
      body: JSON.stringify(paidAt ? { paidAt } : {}),
      ...orgScope(organizationId),
    },
  );
}

// POST /payments/payout/manual. Records payout(s) made OUTSIDE the system: it
// marks the quote(s) as paid and moves no money. Takes a batch and answers one
// result per quote, of which the page reads `status` and `message`.
export function registerManualPayout(quoteIds, { organizationId } = {}) {
  return request('/payments/payout/manual', {
    method: 'POST',
    body: JSON.stringify({ quoteIds }),
    ...orgScope(organizationId),
  });
}

// -- Billing Profiles (unversioned org-domain: /api/billings/profiles) --

export function getBillingProfiles({ organizationId } = {}) {
  return request('/billings/profiles', orgScope(organizationId));
}

export function createBillingProfile(data, { organizationId } = {}) {
  return request('/billings/profiles', {
    method: 'POST',
    body: JSON.stringify(data),
    ...orgScope(organizationId),
  });
}

export function updateBillingProfile(
  billingProfileId,
  data,
  { organizationId } = {},
) {
  return request(`/billings/profiles/${encodeURIComponent(billingProfileId)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...orgScope(organizationId),
  });
}

// -- Bank Accounts (org-domain: /api/billings/bank-accounts) --

export function getBankAccounts({ organizationId } = {}) {
  return request('/billings/bank-accounts', orgScope(organizationId));
}

export function createBankAccount(data, { organizationId } = {}) {
  return request('/billings/bank-accounts', {
    method: 'POST',
    body: JSON.stringify(data),
    ...orgScope(organizationId),
  });
}

export function updateBankAccount(
  bankAccountId,
  data,
  { organizationId } = {},
) {
  return request(
    `/billings/bank-accounts/${encodeURIComponent(bankAccountId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
      ...orgScope(organizationId),
    },
  );
}

export function deleteBankAccount(bankAccountId, { organizationId } = {}) {
  return request(
    `/billings/bank-accounts/${encodeURIComponent(bankAccountId)}`,
    { method: 'DELETE', ...orgScope(organizationId) },
  );
}

// -- Organization profile (DisplayProfile: /api/profile) --

export function getOrgProfile({ organizationId } = {}) {
  return request('/profile', orgScope(organizationId));
}

export function updateOrgProfile(data, { organizationId } = {}) {
  return request('/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
    ...orgScope(organizationId),
  });
}

// -- Pricing --

export function calculatePricing({ quotedAmount, organizationId } = {}) {
  const qs = buildSearch({
    quotedAmount: quotedAmount != null ? String(quotedAmount) : undefined,
  });
  return request(
    `/pricing/calculate${qs ? `?${qs}` : ''}`,
    orgScope(organizationId),
  );
}

// GET /pricing/tiers — the canonical fee-tier table (public):
// { ivaRate, tiers: [{ tier, min, max, netPrice, ivaAmount, totalPrice }] }.
// The open-ended last tier travels with max: null.
export function getPricingTiers() {
  return request('/pricing/tiers');
}

// GET /pricing/usage — the org's charge-side usage: currentTier is the tier
// the NEXT notification pays, counted over the accrued service fees of the
// Chile calendar year, so the Tarifa tab can never disagree with the charge.
export function getPricingUsage({ organizationId } = {}) {
  return request('/pricing/usage', orgScope(organizationId));
}

// -- Notification Actings --

export function getNotificationActing(
  notificationActingId,
  { organizationId } = {},
) {
  return request(
    `/notification-actings/${encodeURIComponent(notificationActingId)}`,
    orgScope(organizationId),
  );
}

export function getNotificationActings({
  caseId,
  startDate,
  endDate,
  page,
  pageSize,
  search,
  sortBy,
  sortKey,
  tribunalId,
  organizationId,
}) {
  const qs = buildSearch({
    caseId,
    startDate,
    endDate,
    currentPage: String(page),
    pageSize: String(pageSize),
    search,
    sortBy,
    sortKey,
    tribunalId,
  });
  return request(`/notification-actings?${qs}`, orgScope(organizationId));
}

// Lightweight per-case active-diligencia summary (status + deadline) for the
// Causas list — one slim query instead of fetching every task and merging.
export function getActiveDiligencias({ organizationId } = {}) {
  return request('/tasks/active-summary', orgScope(organizationId));
}

// -- Dashboard --

// GET /dashboard/inicio?startDate=&endDate= — aggregated home metrics.
// organizationId is injected as the X-Organization-Id header (explicit or via
// apiClient's singleton), never as a query param. Both dates are optional
// (backend defaults to the last 30 days); empty params are omitted by
// buildSearch.
export function getDashboardInicio({
  startDate,
  endDate,
  organizationId,
} = {}) {
  const search = buildSearch({ startDate, endDate });
  return request(
    `/dashboard/inicio${search ? `?${search}` : ''}`,
    orgScope(organizationId),
  );
}

// -- Memberships --

export function listMembers({ organizationId } = {}) {
  return request('/memberships/members', orgScope(organizationId));
}

export function inviteMember(data, { organizationId } = {}) {
  return request('/memberships/members/invite', {
    method: 'POST',
    body: JSON.stringify(data),
    ...orgScope(organizationId),
  });
}

export function updateMemberRole(memberId, data, { organizationId } = {}) {
  return request(`/memberships/members/${encodeURIComponent(memberId)}/role`, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...orgScope(organizationId),
  });
}

export function removeMember(memberId, { organizationId } = {}) {
  return request(`/memberships/members/${encodeURIComponent(memberId)}`, {
    method: 'DELETE',
    ...orgScope(organizationId),
  });
}

// Resends the email of a still-INVITED member's invitation. inviteMember
// creates a new invitation instead.
export function resendInvitation(memberId, { organizationId } = {}) {
  return request(
    `/memberships/members/${encodeURIComponent(memberId)}/resend-invitation`,
    { method: 'POST', ...orgScope(organizationId) },
  );
}

// A non-owner member leaves the organization. The API acts on the caller's
// own membership, so it does not require org:manage-members.
export function leaveOrganization({ organizationId } = {}) {
  return request('/memberships/members/me', {
    method: 'DELETE',
    ...orgScope(organizationId),
  });
}

// -- Receptors --

export function getReceptors({
  tribunalId,
  page = 1,
  pageSize = 100,
  organizationId,
} = {}) {
  const search = buildSearch({
    tribunalId,
    currentPage: String(page),
    pageSize: String(pageSize),
  });
  return request(`/receptors?${search}`, orgScope(organizationId));
}

// -- Poder Judicial directory (courts, tribunals) --

// GET /courts — Cortes de Apelaciones with their nested tribunals.
export function getCourts({ page = 1, pageSize = 100, organizationId } = {}) {
  const searchQs = buildSearch({
    currentPage: String(page),
    pageSize: String(pageSize),
  });
  return request(`/courts?${searchQs}`, orgScope(organizationId));
}

// GET /tribunals — tribunals, optionally filtered by parent court / search.
export function getTribunals({
  page = 1,
  pageSize = 50,
  search,
  courtId,
  sortKey,
  sortBy,
  organizationId,
} = {}) {
  const searchQs = buildSearch({
    currentPage: String(page),
    pageSize: String(pageSize),
    search,
    courtId,
    sortKey,
    sortBy,
  });
  return request(`/tribunals?${searchQs}`, orgScope(organizationId));
}

// -- Auth (current user) --
// NOT org-scoped: the /auth/change-password and /auth/profile routes mount
// authenticate() only (no resolveOrg), so they take no organizationId.

// Change the authenticated user's password. PUT /api/auth/change-password
export function changePassword(data) {
  return request('/auth/change-password', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Update the authenticated user's profile. PUT /api/auth/profile
export function updateProfile(data) {
  return request('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// -- Feedback --

// POST /support-requests — un mensaje del usuario que llega al buzón de soporte
// como correo, con su dirección puesta como responder-a. Es la única superficie
// que le contesta a una persona.
//
// No es lo mismo que /feedback, que abre una tarjeta en el tablero. Ese destino
// es el correcto para el reporte de un problema, que el equipo prioriza y no
// responde; este lo es para una consulta, que espera respuesta.
//
// El cuerpo lleva un solo campo y el servidor lo acota entre 10 y 2000
// caracteres.
export function submitSupportRequest(message, { organizationId } = {}) {
  return request('/support-requests', {
    method: 'POST',
    body: JSON.stringify({ message }),
    ...orgScope(organizationId),
  });
}

export function submitFeedback(payload, { organizationId } = {}) {
  return request('/feedback', {
    method: 'POST',
    body: JSON.stringify(payload),
    ...orgScope(organizationId),
  });
}

// -- Public auth (pre-session) --
//
// These run before there is a session, so they bypass the authenticated
// transport: no org header, and a 401 (bad credentials / expired link) must NOT
// fire emitUnauthorized. Each returns the raw Response so the caller can map
// status → its own UI message (login error codes, "link expired", the 404
// treated as success on password-reset to avoid email enumeration, etc.).
// `signal` is optional and only validate-token passes one (REC-1955).
function authPost(path, body, { signal } = {}) {
  return fetch(`${BACKEND_BASE_URL}${BASE_PATH}/auth${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...(body !== undefined && { body: JSON.stringify(body) }),
    ...(signal && { signal }),
  });
}

// Public catalog read: the TyC version in force (version, title, pdfUrl,
// effectiveDate), or null when none is active yet. GET, so it doesn't go
// through authPost (which always sends a JSON body). Unauthenticated on
// purpose — this is the activate/login flow-init read, distinct from the
// channel-intake:read-gated GET /channels/terms/current used by the
// WhatsApp ops accept flow.
// Bounded so a stalled catalog read rejects and useTermsCatalog can fall back
// instead of hanging on loading forever (Auth activate/login flow-init).
const TERMS_CATALOG_TIMEOUT_MS = 10_000;

export const getCurrentTermsRequest = () =>
  fetch(`${BACKEND_BASE_URL}${BASE_PATH}/auth/terms/current`, {
    credentials: 'include',
    signal: AbortSignal.timeout(TERMS_CATALOG_TIMEOUT_MS),
  });

export const loginRequest = (credentials) => authPost('/login', credentials);
export const logoutRequest = () => authPost('/logout');
// The caller bounds it with `signal` (AuthContext, VALIDATE_TOKEN_TIMEOUT_MS).
export const validateTokenRequest = ({ signal } = {}) =>
  authPost('/validate-token', undefined, { signal });
export const activateAccountRequest = (payload) =>
  authPost('/activate', payload);
export const resendActivationRequest = (payload) =>
  authPost('/resend-activation', payload);
export const requestPasswordResetRequest = (payload) =>
  authPost('/request-password-reset', payload);
export const resetPasswordRequest = (payload) =>
  authPost('/reset-password', payload);

// -- Public invitations (pre-session) --
//
// The invitation flow runs before any session exists, like public auth above,
// but under /memberships/invitations. authPost hardcodes the /auth prefix, so
// these build the request themselves with its shape: raw Response,
// credentials included, no X-Organization-Id.
function invitationsPost(path, body) {
  return fetch(
    `${BACKEND_BASE_URL}${BASE_PATH}/memberships/invitations${path}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export const invitationPreviewRequest = (token) =>
  fetch(
    `${BACKEND_BASE_URL}${BASE_PATH}/memberships/invitations/preview?token=${encodeURIComponent(token)}`,
    { credentials: 'include' },
  );
export const acceptInvitationRequest = (payload) =>
  invitationsPost('/accept', payload);
export const rejectInvitationRequest = (token) =>
  invitationsPost('/reject', { token });
